import React from "react";
import { Link } from "react-router-dom";
import {
  KeyRound, Plane, Bell, Sailboat, Sparkles, Wine, Mountain, HeartHandshake,
} from "lucide-react";

const SERVICES = [
  {
    icon: KeyRound,
    title: "Accommodation",
    items: [
      "Luxury villa sourcing",
      "Boutique hotel reservations",
      "Property management for owners",
      "Villa preparation before arrival",
    ],
  },
  {
    icon: Plane,
    title: "Arrival",
    items: [
      "VIP airport meet and greet",
      "Private transfers",
      "Chauffeur service",
      "Luxury vehicle hire",
      "Private jet and helicopter coordination",
    ],
  },
  {
    icon: Bell,
    title: "At Your Villa",
    items: [
      "Private chefs",
      "Daily housekeeping",
      "Butler service",
      "Grocery and wine stocking",
      "Fresh flowers",
      "Childcare",
      "Laundry",
      "Security",
    ],
  },
  {
    icon: Sailboat,
    title: "Sea Experiences",
    items: [
      "Yacht charters",
      "Sailing trips",
      "Sunset cruises",
      "Fishing excursions",
      "Paddleboarding and kayaking",
      "Scuba diving",
    ],
  },
  {
    icon: Sparkles,
    title: "Wellness",
    items: [
      "Massage",
      "Yoga",
      "Pilates",
      "Personal trainers",
      "Nutrition coaching",
      "Beauty treatments",
    ],
  },
  {
    icon: Wine,
    title: "Dining",
    items: [
      "Restaurant reservations",
      "Wine tastings",
      "Vineyard visits",
      "Private beach dining",
      "Chef's table experiences",
    ],
  },
  {
    icon: Mountain,
    title: "Adventure",
    items: [
      "Guided hikes",
      "Cycling tours",
      "Horse riding",
      "Caving",
      "Photography tours",
      "Local artisan experiences",
    ],
  },
  {
    icon: HeartHandshake,
    title: "Celebrations",
    items: [
      "Proposals",
      "Weddings",
      "Anniversaries",
      "Family reunions",
      "Corporate retreats",
    ],
  },
];

export default function HomePage() {
  return (
    <div className="grain">
      {/* HERO — Menorca luxury estate */}
      <section className="relative min-h-screen w-full overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 kenburns">
          <img
            src="https://customer-assets.emergentagent.com/job_island-elegance/artifacts/5pza09s3_Property-fa3b93d85c2392c02bce997fad5bfc95-130380355.jpg"
            alt="Menorca luxury estate pool"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/35 to-[var(--ml-bg)]" />
        <div className="absolute inset-0 vignette pointer-events-none" />

        <div className="relative z-10 container-ml min-h-screen flex flex-col justify-end pb-24 md:pb-32">
          <div className="reveal-up max-w-6xl">
            <p className="overline mb-6 md:mb-10">Menorca · Luxury</p>
            <h1 className="font-serif-ml font-light leading-[0.9] tracking-[-0.02em] text-[var(--ml-text)] text-5xl sm:text-6xl md:text-8xl lg:text-[9rem]">
              Extraordinary Menorca,<br />
              <em className="text-[var(--ml-gold)] font-light">effortlessly arranged.</em>
            </h1>
          </div>
        </div>
      </section>

      {/* OUR SERVICES */}
      <section className="container-ml pb-24 md:pb-32 pt-24" data-testid="services-section">
        <div className="text-center mb-16 md:mb-24">
          <p className="overline mb-6">Our Services</p>
          <h2 className="font-serif-ml text-5xl md:text-7xl lg:text-8xl tracking-[-0.02em] leading-[0.9] text-[var(--ml-text)]">
            Everything, <em className="text-[var(--ml-gold)]">arranged.</em>
          </h2>
          <p className="font-serif-ml italic text-lg md:text-2xl text-[var(--ml-text-dim)] mt-8 max-w-2xl mx-auto leading-snug">
            From the villa keys to the flowers on your table — a single, discreet number for every detail of your Menorquín summer.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
          {SERVICES.map((svc, idx) => {
            const Icon = svc.icon;
            return (
              <div
                key={svc.title}
                data-testid={`service-${svc.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-[var(--ml-bg)] p-8 md:p-10 group hover:bg-[#0a0a0a] transition-colors duration-500"
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono-ml text-[0.6rem] tracking-[0.3em] uppercase text-[var(--ml-gold)]">
                    № {String(idx + 1).padStart(2, "0")}
                  </span>
                  <Icon size={22} className="text-[var(--ml-gold)] group-hover:scale-110 transition-transform duration-500" strokeWidth={1.2} />
                </div>
                <h3 className="font-serif-ml text-3xl md:text-4xl tracking-tight leading-[1] text-[var(--ml-text)] mb-6">
                  {svc.title}
                </h3>
                <div className="h-px w-10 bg-[var(--ml-gold)] mb-6" />
                <ul className="space-y-3">
                  {svc.items.map(it => (
                    <li key={it} className="text-sm text-[var(--ml-text-dim)] leading-relaxed flex items-start gap-2.5">
                      <span className="text-[var(--ml-gold)] leading-tight select-none">·</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* CONCIERGE CTA */}
      <section className="container-ml py-24 md:py-40 text-center" data-testid="concierge-cta-section">
        <p className="overline mb-8">Concierge</p>
        <h2 className="font-serif-ml text-5xl md:text-8xl tracking-[-0.02em] leading-[0.9] text-[var(--ml-text)] mb-10">
          The concierge is<br/><em className="text-[var(--ml-gold)]">at your service.</em>
        </h2>
        <p className="text-[var(--ml-text-dim)] text-lg max-w-2xl mx-auto mb-12 leading-relaxed">
          For private bookings, custom itineraries, and access to the addresses we do not publish — please write. We respond, within 24 hours.
        </p>
        <Link to="/concierge" data-testid="cta-write-concierge" className="btn-ml-gold inline-block">
          Write to the concierge
        </Link>
      </section>
    </div>
  );
}
